import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
  /** Optional auto-dismiss ms; 0 = sticky (user closes manually). Default 4000. */
  durationMs?: number;
}

interface ToastApi {
  toast: (message: string, variant?: ToastVariant, durationMs?: number) => void;
  success: (message: string, durationMs?: number) => void;
  error: (message: string, durationMs?: number) => void;
  info: (message: string, durationMs?: number) => void;
  warning: (message: string, durationMs?: number) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Wrap the app in this provider once (in `main.tsx`) and call `useToast()` from any
 * component. Toasts queue independently — call multiple in a row and they stack.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, variant: ToastVariant = 'info', durationMs = 4000) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, message, variant, durationMs }]);
    },
    []
  );

  const api: ToastApi = {
    toast,
    success: (m, d) => toast(m, 'success', d),
    error: (m, d) => toast(m, 'error', d ?? 6000),
    info: (m, d) => toast(m, 'info', d),
    warning: (m, d) => toast(m, 'warning', d),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastTray toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const variantStyles: Record<ToastVariant, string> = {
  success: 'bg-green-50 border-green-200 text-green-900',
  error: 'bg-red-50 border-red-200 text-red-900',
  info: 'bg-slate-50 border-slate-200 text-slate-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
};

function ToastTray({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  useEffect(() => {
    const ms = toast.durationMs ?? 4000;
    if (ms <= 0) return;
    const timer = window.setTimeout(() => onDismiss(toast.id), ms);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.durationMs, onDismiss]);

  return (
    <div
      role="status"
      className={`border rounded-md shadow px-4 py-3 text-sm flex items-start gap-3 ${variantStyles[toast.variant]}`}
    >
      <span className="flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="text-current opacity-60 hover:opacity-100 leading-none"
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  );
}
